import { NavigateFunction } from "react-router-dom";
import RouterConstants from "../constants/RouterConstants";
import useSecureNavigate from "../hooks/useSecureNavigate";
import { useAppStore } from "../hooks/useStores";
import { OrgFeatures } from "../models/billing";
import { UserOrganization } from "../models/organization";

export const usePluginsUtils = () => {
	const { authStore, tgStore, appStore } = useAppStore();
	const navigate = useSecureNavigate();
	const navigateTo = (navigate: NavigateFunction, url: string, success: boolean) => {
		const formattedUrl = url.startsWith("/") ? url : `/${url}`;
		navigate(formattedUrl, { state: { success } });
	};

	const verifyPluginAccess = async (selectedOrg: UserOrganization | null = null) => {
		authStore.isPluginLoginLoading = true;

		try {
			const userOrgs = await authStore.loadOrganizations();
			// Use the provided organization or default to the first one
			const orgToUse = selectedOrg || (userOrgs.length > 0 ? userOrgs[0] : null);

			if (!authStore.user || !orgToUse) {
				navigateTo(navigate, RouterConstants.PLUGIN_LOGIN_STATUS.path, false);
				return;
			}

			const orgSubscription: any = await authStore.loadOrgSubscription();
			const hasTGAccess = orgSubscription?.features?.includes(OrgFeatures.TAILORED_GENERATION) ?? false;

			const loadedModels = await tgStore.tgModelStore.listAllModels("Completed");

			if (!(hasTGAccess && loadedModels.length > 0)) {
				navigateTo(navigate, RouterConstants.PLUGIN_LOGIN_STATUS.path, false);
				return;
			}

			if (authStore.pluginAuthId) {
				const isRegistered = await appStore.registerPluginId(authStore.pluginAuthId);
				navigateTo(navigate, RouterConstants.PLUGIN_LOGIN_STATUS.path, isRegistered);
			} else {
				navigateTo(navigate, RouterConstants.PLUGIN_LOGIN_STATUS.path, false);
			}
		} catch (error) {
			console.error("Error checking TG access:", error);
			navigateTo(navigate, RouterConstants.PLUGIN_LOGIN_STATUS.path, false);
		} finally {
			authStore.isPluginLoginLoading = false;
		}
	};

	const handleSelectOrganization = async () => {
		const userOrgs = await authStore.loadOrganizations();
		if (userOrgs.length === 1) {
			verifyPluginAccess(userOrgs[0]);
		} else {
			if (authStore.selectedOrganization) {
				verifyPluginAccess(authStore.selectedOrganization);
			} else {
				authStore.handleShowSelectOrganizationPopup(true);
			}
		}
	};

	return {
		verifyPluginAccess,
		handleSelectOrganization
	};
};
